import * as React from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styled from "styled-components";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.locale("ko");

interface YearMonthSelectorProps {
  currentDate: dayjs.Dayjs;
  onDateChange: (date: dayjs.Dayjs) => void;
  minYear?: number;
  maxYear?: number;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0;
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${({ disabled }) => (disabled ? "#434859" : "#e0e1e8")};
  font-size: 20px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:active {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const RangeText = styled.div`
  flex: 1;
  text-align: center;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: -0.2px;
  line-height: 20px;
  margin: 0 8px;
  white-space: nowrap;
`;

const MonthText = styled.span`
  font-size: 18px;
  font-weight: bold;
  letter-spacing: -0.2px;
  line-height: 20px;
`;

const DotText = styled.span`
  font-size: 18px;
  font-weight: bold;
  letter-spacing: -0.2px;
  line-height: 20px;
  margin-left: 5px;
  color: rgba(255, 255, 255, 0.9);
`;

export function YearMonthSelector({
  currentDate,
  onDateChange,
  minYear,
  maxYear,
}: YearMonthSelectorProps) {
  const today = dayjs();
  const currentYear = today.year();

  const startYearAdjusted = minYear || currentYear - 1;

  const minDate = React.useMemo(
    () => dayjs().year(startYearAdjusted).month(0).date(1),
    [startYearAdjusted]
  );
  const isPreviousMonthDisabled = currentDate.isSameOrBefore(minDate, "month");
  const isNextMonthDisabled = currentDate.isSameOrAfter(today, "month");

  const isCurrentMonth =
    currentDate.year() === today.year() &&
    currentDate.month() === today.month();

  const handlePreviousMonth = () => {
    const prevMonth = currentDate.subtract(1, "month");
    if (prevMonth.isSameOrAfter(minDate, "month")) {
      onDateChange(prevMonth);
    }
  };

  const handleNextMonth = () => {
    const nextMonth = currentDate.add(1, "month");
    if (nextMonth.isSameOrBefore(today, "month")) {
      onDateChange(nextMonth);
    }
  };

  return (
    <Container>
      <NavButton
        onClick={handlePreviousMonth}
        disabled={isPreviousMonthDisabled}
        aria-label="이전 달"
      >
        <ChevronLeft size={24} />
      </NavButton>
      <RangeText>
        <MonthText>{currentDate.format("YYYY년 M월")}</MonthText>
        {isCurrentMonth && <DotText>· 이번 달</DotText>}
      </RangeText>
      <NavButton
        onClick={handleNextMonth}
        disabled={isNextMonthDisabled}
        aria-label="다음 달"
      >
        <ChevronRight size={24} />
      </NavButton>
    </Container>
  );
}
